import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const RECURRENCE_OPTIONS = [
  { value: 'once', label: 'Once' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const WINDOW_OPTIONS = [
  { value: 'next_day', label: 'Next day' },
  { value: 'next_7_days', label: 'Next 7 days' },
  { value: 'next_week', label: 'Next calendar week (Mon–Sun)' },
  { value: 'next_month', label: 'Next month' },
  { value: 'next_quarter', label: 'Next quarter' },
  { value: 'today', label: 'Same day' },
  { value: 'custom', label: 'Custom day offsets' },
];

const emptyForm = (jobType) => ({
  name: '',
  enabled: true,
  all_stations: true,
  station_ids: [],
  run_time: '23:00',
  recurrence: 'daily',
  day_of_week: 5,
  day_of_month: 1,
  quarter_month: 1,
  run_on_date: '',
  target_window: 'next_day',
  target_start_offset_days: 1,
  target_end_offset_days: 1,
  start_hour: 0,
  end_hour: 23,
  include_traffic: false,
  job_type: jobType,
});

function formatWhen(schedule) {
  if (!schedule?.next_run_at) return '—';
  return new Date(schedule.next_run_at).toLocaleString();
}

function formatLastRun(schedule) {
  if (!schedule?.last_run_at) return 'Never';
  const when = new Date(schedule.last_run_at).toLocaleString();
  return schedule.last_run_status ? `${when} (${schedule.last_run_status})` : when;
}

export default function AutoGenerationSchedules({ jobType, stations = [], canEdit = false, onSchedulesChange }) {
  const isPlaylist = jobType === 'playlist';
  const [schedules, setSchedules] = useState([]);
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(jobType));
  const [saving, setSaving] = useState(false);
  const onSchedulesChangeRef = useRef(onSchedulesChange);
  onSchedulesChangeRef.current = onSchedulesChange;

  const title = isPlaylist ? 'Automatic station logs' : 'Automatic advertising schedule';
  const itemLabel = isPlaylist ? 'station logs' : 'advertising';

  const stationName = useCallback((id) => {
    return stations.find((station) => Number(station.id) === Number(id))?.name || `Station ${id}`;
  }, [stations]);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auto-generation-schedules`, {
        params: { job_type: jobType },
        withCredentials: true,
      });
      const list = Array.isArray(res.data?.schedules) ? res.data.schedules : [];
      setSchedules(list);
      setTimezone(res.data?.timezone || '');
      onSchedulesChangeRef.current?.(list);
    } catch (error) {
      console.error('Failed to load generation schedules:', error);
    } finally {
      setLoading(false);
    }
  }, [jobType]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(jobType));
    setDialogOpen(true);
  };

  const openEdit = (schedule) => {
    setEditing(schedule);
    setForm({
      ...emptyForm(jobType),
      ...schedule,
      all_stations: !schedule.station_ids?.length,
      station_ids: schedule.station_ids || [],
      run_time: String(schedule.run_time || '23:00').slice(0, 5),
      day_of_week: schedule.day_of_week ?? 5,
      day_of_month: schedule.day_of_month ?? 1,
      quarter_month: schedule.quarter_month ?? 1,
      run_on_date: schedule.run_on_date || '',
    });
    setDialogOpen(true);
  };

  const payloadFromForm = () => ({
    name: form.name.trim(),
    job_type: jobType,
    enabled: form.enabled !== false,
    station_ids: form.all_stations ? [] : form.station_ids,
    run_time: form.run_time,
    recurrence: form.recurrence,
    day_of_week: form.recurrence === 'weekly' ? form.day_of_week : null,
    day_of_month: form.recurrence === 'monthly' || form.recurrence === 'quarterly' ? form.day_of_month : null,
    quarter_month: form.recurrence === 'quarterly' ? form.quarter_month : null,
    run_on_date: form.recurrence === 'once' ? form.run_on_date : null,
    target_window: form.target_window,
    target_start_offset_days: Number(form.target_start_offset_days),
    target_end_offset_days: Number(form.target_end_offset_days),
    start_hour: Number(form.start_hour),
    end_hour: Number(form.end_hour),
    include_traffic: isPlaylist && form.include_traffic === true,
  });

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Please enter a name');
      return;
    }
    if (!form.all_stations && (!form.station_ids || form.station_ids.length === 0)) {
      alert('Select at least one station, or choose all stations');
      return;
    }
    if (form.recurrence === 'once' && !form.run_on_date) {
      alert('Choose the date this should run');
      return;
    }
    setSaving(true);
    try {
      const body = payloadFromForm();
      if (editing) {
        await axios.put(`${API_URL}/api/auto-generation-schedules/${editing.id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/auto-generation-schedules`, body, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (schedule) => {
    if (!window.confirm(`Delete schedule “${schedule.name}”? Manual generate will still work.`)) return;
    try {
      await axios.delete(`${API_URL}/api/auto-generation-schedules/${schedule.id}`, { withCredentials: true });
      fetchSchedules();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete schedule');
    }
  };

  const handleToggle = async (schedule, enabled) => {
    try {
      await axios.put(`${API_URL}/api/auto-generation-schedules/${schedule.id}`, {
        enabled,
      }, { withCredentials: true });
      fetchSchedules();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update schedule');
    }
  };

  const handleRunNow = async (schedule) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/auto-generation-schedules/${schedule.id}/run-now`,
        {},
        { withCredentials: true }
      );
      const position = res.data?.queue_position;
      alert(position
        ? `Queued (position #${position}). Progress appears in the background job widget.`
        : 'Queued. Progress appears in the background job widget.');
      fetchSchedules();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to queue generation');
    }
  };

  const stationLabel = (schedule) => {
    if (!schedule.station_ids?.length) return 'All stations';
    return schedule.station_ids.map(stationName).join(', ');
  };

  const helper = useMemo(() => (
    isPlaylist
      ? 'Manual Generate Playlist still works for internal stations, but you will be warned first while a schedule is enabled. These runs fire on the server at the time you set. Stations using an external scheduler pull matching hour logs (Dropbox, local folder, or FTP/SFTP) for that date/hour window instead of generating from clocks.'
      : 'Keep using Generate Schedule whenever you want. These runs fire on the server at the time you set, so nobody needs to log in.'
  ), [isPlaylist]);

  return (
    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ScheduleIcon sx={{ color: '#22d3ee' }} />
              <Typography variant="h6" sx={{ color: '#22d3ee' }}>{title}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {helper}
              {timezone ? ` Times use the server timezone (${timezone}).` : ''}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!canEdit}
            onClick={openCreate}
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
          >
            Add schedule
          </Button>
        </Box>

        {schedules.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 1 }}>
            No automatic {itemLabel} yet. Example: 23:00 daily for the next day, or 22:00 every Friday for next week.
          </Alert>
        )}

        {schedules.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Stations</TableCell>
                  <TableCell>Next run</TableCell>
                  <TableCell>Last run</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{schedule.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{schedule.summary}</Typography>
                    </TableCell>
                    <TableCell>{stationLabel(schedule)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={schedule.enabled ? formatWhen(schedule) : 'Disabled'}
                        sx={{ bgcolor: 'rgba(34,211,238,0.08)' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={schedule.last_run_message || ''}>
                        <span>{formatLastRun(schedule)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={schedule.enabled !== false}
                        disabled={!canEdit}
                        onChange={(e) => handleToggle(schedule, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Run now">
                        <span>
                          <IconButton size="small" disabled={!canEdit} onClick={() => handleRunNow(schedule)}>
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton size="small" disabled={!canEdit} onClick={() => openEdit(schedule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" disabled={!canEdit} onClick={() => handleDelete(schedule)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit schedule' : 'Add schedule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isPlaylist ? 'e.g. Overnight logs' : 'e.g. Overnight ads'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Repeats</InputLabel>
                <Select
                  value={form.recurrence}
                  label="Repeats"
                  onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                >
                  {RECURRENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Run time"
                type="time"
                value={form.run_time}
                onChange={(e) => setForm({ ...form, run_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {form.recurrence === 'once' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Run on date"
                  type="date"
                  value={form.run_on_date}
                  onChange={(e) => setForm({ ...form, run_on_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {form.recurrence === 'weekly' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Day of week</InputLabel>
                  <Select
                    value={form.day_of_week}
                    label="Day of week"
                    onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                  >
                    {WEEKDAYS.map((day) => (
                      <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {(form.recurrence === 'monthly' || form.recurrence === 'quarterly') && (
              <Grid item xs={12} sm={form.recurrence === 'quarterly' ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Day of month"
                  type="number"
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value, 10) || 1 })}
                  InputProps={{ inputProps: { min: 1, max: 31 } }}
                />
              </Grid>
            )}
            {form.recurrence === 'quarterly' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Month of quarter</InputLabel>
                  <Select
                    value={form.quarter_month}
                    label="Month of quarter"
                    onChange={(e) => setForm({ ...form, quarter_month: e.target.value })}
                  >
                    <MenuItem value={1}>1st (Jan / Apr / Jul / Oct)</MenuItem>
                    <MenuItem value={2}>2nd (Feb / May / Aug / Nov)</MenuItem>
                    <MenuItem value={3}>3rd (Mar / Jun / Sep / Dec)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Generate for</InputLabel>
                <Select
                  value={form.target_window}
                  label="Generate for"
                  onChange={(e) => setForm({ ...form, target_window: e.target.value })}
                >
                  {WINDOW_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {form.target_window === 'custom' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start offset (days)"
                    type="number"
                    value={form.target_start_offset_days}
                    onChange={(e) => setForm({ ...form, target_start_offset_days: parseInt(e.target.value, 10) || 0 })}
                    helperText="0 = today, 1 = tomorrow"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End offset (days)"
                    type="number"
                    value={form.target_end_offset_days}
                    onChange={(e) => setForm({ ...form, target_end_offset_days: parseInt(e.target.value, 10) || 0 })}
                    helperText="Inclusive end of the range"
                  />
                </Grid>
              </>
            )}
            {isPlaylist && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start hour"
                    type="number"
                    value={form.start_hour}
                    onChange={(e) => setForm({ ...form, start_hour: parseInt(e.target.value, 10) || 0 })}
                    InputProps={{ inputProps: { min: 0, max: 23 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End hour"
                    type="number"
                    value={form.end_hour}
                    onChange={(e) => setForm({ ...form, end_hour: parseInt(e.target.value, 10) || 0 })}
                    InputProps={{ inputProps: { min: 0, max: 23 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.include_traffic === true}
                        onChange={(e) => setForm({ ...form, include_traffic: e.target.checked })}
                      />
                    }
                    label="Generate advertising for the same dates first"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Scheduled runs overwrite existing logs for those dates and hours.
                  </Typography>
                </Grid>
              </>
            )}
            {stations.length > 1 && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.all_stations}
                        onChange={(e) => setForm({ ...form, all_stations: e.target.checked, station_ids: [] })}
                      />
                    }
                    label="All active stations"
                  />
                </Grid>
                {!form.all_stations && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Stations</InputLabel>
                      <Select
                        multiple
                        value={form.station_ids.map(Number)}
                        label="Stations"
                        onChange={(e) => setForm({
                          ...form,
                          station_ids: (Array.isArray(e.target.value) ? e.target.value : [e.target.value]).map(Number),
                        })}
                        renderValue={(selected) => selected.map(stationName).join(', ')}
                      >
                        {stations.map((station) => (
                          <MenuItem key={station.id} value={Number(station.id)}>{station.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled !== false}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  />
                }
                label="Enabled"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#22d3ee', color: '#04222b' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
